<template>
  <modal-dialog
    ref="dialog"
    :visible="dialog"
    :title="$t('button.setup_floorplan')"
    persistent
    scrollable
    class="w-100 pa-0"
    :fullscreen="compact"
    max-width="100%"
    card-class="pa-0"
    content-class="dialog-height-100"
    container-class="pa-0 h-100"
    fluid
  >
    <div class="fp-edit-container">
      <floorplan-edit-sidebar
        v-if="sidebarVisible"
        :class="['fp-edit-sidebar', {'compact': compact}]"
        :tab-items="tabItems"
        :selected-tab-item-ids="selectedTabItemIds"
        @place-tabitem="placeTabItem"
        @selected="tabItemSelected"
      />
      <v-divider
        v-if="!sidebarVisible && compact || !compact"
        vertical
      />
      <floorplan-canvas
        ref="fpCanvas"
        :tab-items.sync="tabItems"
        :selected-tab-item-ids="selectedTabItemIds"
        editable
        :zoom="zoom"
        @selected="tabItemSelected"
      />
    </div>
    <v-divider />

    <!-- Actions -->
    <template #actions>
      <v-btn
        v-if="compact"
        text
        class="mr-5"
        primary
        @click="sidebarToggle = !sidebarToggle"
      >
        {{ sidebarVisible ? $t('button.show_floorplan') : $t('button.show_tables') }}
      </v-btn>
      <v-select
        v-model="zoom"
        class="pt-0 mt-0"
        :style="`min-width:${$vuetify.breakpoint.xsOnly ? 42 : 78 }px!important;
        max-width:${$vuetify.breakpoint.xsOnly ? 42 : 78 }px!important`"
        append-icon=""
        :prepend-inner-icon="$vuetify.breakpoint.xsOnly ? '' : 'mdi-magnify-plus'"
        hide-details
        :items="zoomLevels"
      />
      <v-spacer />
      <v-btn
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="confirmCancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import Tab from '@/model/Tab';
import TabItem from '@/model/TabItem';
import { cloneModel, isModelEqualToModel, uniqueMinId } from '@/model/model-utils';
import { performSaveAction, confirmLeaveWithoutSaving } from '@/services/vue-utils';

import FloorplanCanvas from '@/components/views/floorplan/FloorplanCanvas.vue';
import FloorplanEditSidebar from '@/components/views/floorplan/FloorplanEditSidebar.vue';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';

export default Vue.extend({
  name: 'FloorplanEditDialog',
  components: { FloorplanCanvas, FloorplanEditSidebar },
  mixins: [DialogClosable],
  props: {
    tab: { type: Object as PropType<Tab>, required: true },
  },
  data() {
    return {
      tabItems: [] as TabItem[],
      oldTabItems: [] as TabItem[],
      selectedTabItemIds: [] as number[],
      sidebarToggle: false,

      zoom: 1,
      zoomLevels: [
        { value: 0.25, text: '25%' },
        { value: 0.5, text: '50%' },
        { value: 0.75, text: '75%' },
        { value: 1, text: '100%' },
        { value: 1.25, text: '125%' },
        { value: 1.50, text: '150%' },
        { value: 1.75, text: '175%' }],
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.tabItems, this.oldTabItems);
    },
    sidebarVisible(): boolean {
      return !this.compact || this.sidebarToggle;
    },
    compact(): boolean {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  watch: {
    visible(value: boolean) {
      if (!value) return;
      this.reset();
    },
  },
  methods: {
    reset() {
      const tab = this.$tstore.getters.tabsById.get(this.tab.id) as Tab | undefined;
      this.tabItems = (tab?.tabItems ?? []).map((o) => cloneModel(o));
      this.oldTabItems = tab?.tabItems ?? [];
    },
    tabItemSelected(tabItemId: number) {
      this.selectedTabItemIds = [tabItemId];
      this.sidebarToggle = false;
    },
    placeTabItem(i: number, t: TabItem) {
      (this.$refs.fpCanvas as any).placeTable(t);
    },
    close() {
      this.$emit('close');
    },
    confirmCancel() {
      showConfirmationDialog(
        this.$i18n.t('title.confirmation'),
        this.$i18n.t('message.confirm_leave_without_saving'),
        { title: this.$i18n.tc('button.yes'), action: () => this.cancel() },
        { title: this.$i18n.tc('button.cancel'), action: () => {} },
      );
    },
    cancel() {
      this.close();
    },
    async save() {
      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendFloorplanTabItems', {
          entities: this.tabItems,
          tabId: this.tab.id,
        }),
      );

      if (ok) {
        this.close();
      }
    },
  },
});
</script>

<style lang="scss" scoped>

  .fp-edit-container {
    display: flex;
    padding: 0;
    height: calc(100% - 2px);
  }

  .fp-edit-sidebar {
    width: 26%;
    height: 100%;
    overflow: auto;

    &.compact {
      width: 100%;
      min-width: 100%;
    }

  }

</style>
